import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { WorkflowDefinition } from '../../../types/workflows';
import { WorkflowNode } from '../nodes/WorkflowNode';
import { NodePalette } from '../nodes/NodePalette';
import { NodeConfigPanel } from '../nodes/config/NodeConfigPanel';
import { WorkflowSettingsPanel } from '../WorkflowSettingsPanel';
import { CanvasControls } from './CanvasControls';
import { CanvasGrid } from './CanvasGrid';
import { WorkflowEdges } from '../edges/WorkflowEdges';
import { useCanvasInteractions } from '../../../hooks/workflows/useCanvasInteractions';
import { useWorkflowOperations, CanvasNode, CanvasEdge } from '../../../hooks/workflows/useWorkflowOperations';
import { CANVAS_CONSTANTS } from '../../../constants/workflows';

interface WorkflowCanvasProps {
  workflow: WorkflowDefinition;
  onChange: (workflow: WorkflowDefinition) => void;
  isLoading: boolean;
}

const START_NODE: CanvasNode = {
  id: CANVAS_CONSTANTS.START_NODE_ID,
  type: 'StartNode',
  position: CANVAS_CONSTANTS.START_NODE_POSITION,
  data: {}
}

const WorkflowCanvasComponent: React.FC<WorkflowCanvasProps> = ({
  workflow,
  onChange,
  isLoading
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<CanvasNode[]>([]);
  const [edges, setEdges] = useState<CanvasEdge[]>([]);
  const [showNodePalette, setShowNodePalette] = useState(false);
  const [showWorkflowSettings, setShowWorkflowSettings] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [connectToNodeId, setConnectToNodeId] = useState<string | null>(null);

  // Custom hooks
  const {
    zoom,
    pan,
    isPanning,
    handleCanvasMouseDown,
    handleZoomIn,
    handleZoomOut,
    handleResetView,
    handleWheel
  } = useCanvasInteractions(canvasRef);

  const {
    selectedNode,
    setSelectedNode,
    handleNodeDrag,
    handleNodeDragEnd,
    handleNodeAdd,
    handleNodeAddAndConnect,
    handleNodeDelete,
    handleBackgroundClick
  } = useWorkflowOperations({ workflow, onChange });

  const { canvasNodes: memoizedNodes, canvasEdges: memoizedEdges } = useMemo(() => {
    const canvasNodes: CanvasNode[] = (workflow.graph?.nodes || []).map((node, index) => ({
      id: node.node_id,
      type: node.config?.activity_name || 'unknown',
      position: node.config?.position || {
        x: 350,
        y: 120 + (index * 200)
      },
      data: {
        name: node.name,
        config: node.config
      }
    }));

    const canvasEdges: CanvasEdge[] = (workflow.graph?.edges || []).map((edge) => ({
      id: `${edge.condition.type}-${edge.source_node_id}-${edge.destination_node_id}`,
      source: edge.source_node_id,
      target: edge.destination_node_id,
      condition: edge.condition
    }));

    // Add edge from start node to first node if nodes exist and edge doesn't already exist
    if (canvasNodes.length > 0) {
      const firstNodeId = canvasNodes[0].id;
      const hasStartEdge = canvasEdges.some(edge => edge.source === '-1' && edge.target === firstNodeId);
      
      if (!hasStartEdge) {
        canvasEdges.unshift({
          id: 'start-to-first',
          source: '-1',
          target: firstNodeId,
          condition: { type: 'always' }
        });
      }
    }

    return { canvasNodes, canvasEdges };
  }, [workflow.graph?.edges, workflow.graph?.nodes]);

  useEffect(() => {
    // Don't update from workflow if we're currently dragging
    if (isDragging) return;
    
    setNodes(memoizedNodes);
    setEdges(memoizedEdges);
  }, [memoizedNodes, memoizedEdges, isDragging]);

  // Local event handlers
  const handleNodeAddAfter = useCallback(() => {
    setConnectToNodeId(null);
    setShowNodePalette(true);
  }, []);

  const handleCreateNodeForConnection = useCallback((sourceNodeId: string) => {
    setConnectToNodeId(sourceNodeId);
    setShowNodePalette(true);
  }, []);

  const handleCreateAndConnectNode = useCallback((nodeType: string, sourceNodeId: string, condition?: any) => {
    const result = handleNodeAddAndConnect(nodeType, sourceNodeId, condition, nodes, edges);
    setNodes(result.nodes);
    setEdges(result.edges);
    setShowNodePalette(false);
    // Automatically select the new node for configuration
    setSelectedNode(result.newNodeId);
  }, [edges, handleNodeAddAndConnect, nodes, setSelectedNode]);

  const handleLocalNodeAdd = useCallback((nodeType: string, position?: { x: number; y: number }) => {
    if (connectToNodeId) {
      // Create and connect to specified node
      handleCreateAndConnectNode(nodeType, connectToNodeId, { type: 'always' });
      setConnectToNodeId(null);
    } else {
      // Regular node creation
      const updatedNodes = handleNodeAdd(nodeType, nodes, edges, position);
      setNodes(updatedNodes);
      setShowNodePalette(false);
    }
  }, [connectToNodeId, edges, handleCreateAndConnectNode, handleNodeAdd, nodes]);

  const handleLocalNodeDrag = useCallback((nodeId: string, newPosition: { x: number; y: number }) => {
    // Set dragging state to prevent workflow updates
    setIsDragging(true);
    
    // Adjust position to account for canvas transforms (zoom and pan)
    const adjustedPosition = {
      x: (newPosition.x - pan.x) / zoom,
      y: (newPosition.y - pan.y) / zoom
    };
    
    const updatedNodes = handleNodeDrag(nodeId, adjustedPosition, nodes);
    setNodes(updatedNodes);
  }, [handleNodeDrag, nodes, pan.x, pan.y, zoom]);

  const handleLocalNodeDragEnd = useCallback(() => {
    // Save the workflow when drag ends and clear dragging state
    setIsDragging(false);
    handleNodeDragEnd(nodes, edges);
  }, [edges, handleNodeDragEnd, nodes]);

  const handleLocalNodeDelete = useCallback((nodeId: string) => {
    const result = handleNodeDelete(nodeId, nodes, edges);
    setNodes(result.nodes);
    setEdges(result.edges);
  }, [edges, handleNodeDelete, nodes]);

  const handleLocalEdgeUpdate = useCallback((updatedEdges: CanvasEdge[]) => {
    setEdges(updatedEdges);
  }, []);

  const handleResize = useCallback(() => {
    setViewportWidth(window.innerWidth);
  }, []);

  // Handle viewport resize for responsive edge positioning
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return (
    <div 
      className="h-full w-full flex relative overflow-hidden"
      role="application"
      aria-label="Workflow builder"
    >
      {/* Canvas */}
      <div 
        ref={canvasRef}
        className={`workflow-canvas flex-1 bg-gray-50 relative border-2 border-red-200 overflow-hidden ${
          isPanning ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={(e) => {
          // Only handle canvas panning if not clicking on a workflow-node
          const isClickingOnNode = (e.target as HTMLElement).closest('.workflow-node');
          if (!isClickingOnNode) {
            handleCanvasMouseDown(e, handleBackgroundClick);
          }
        }}
        onWheel={handleWheel}
        style={{
          minHeight: '500px',
          height: '100%',
          width: '100%'
        }}
        role="region"
        aria-label="Workflow canvas"
        tabIndex={0}
      >
        <CanvasGrid zoom={zoom} pan={pan} />

        {/* Canvas content with transform - memoized for performance */}
        <div
          style={useMemo(() => ({
            transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
            transformOrigin: '0 0',
            width: '100%',
            height: '100%',
            position: 'relative'
          }), [zoom, pan.x, pan.y])}
        >

        {/* Empty state when no nodes */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <Plus className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Start Building Your Workflow</h3>
              <p className="text-gray-600 max-w-md">
                Click the + button to add nodes to your workflow canvas
              </p>
            </div>
          </div>
        )}

        <WorkflowEdges 
          nodes={nodes} 
          edges={edges} 
          startNode={START_NODE}
          viewportWidth={viewportWidth}
        />

        {/* Nodes */}
        {useMemo(() => {
          return [START_NODE, ...nodes].map((node, index) => {      
            const pos = {...node.position};
            return (
              <WorkflowNode
                key={node.id}
                id={node.id}
                type={node.type}
                position={pos}
                data={node.data}
                selected={selectedNode === node.id}
                onSelect={() => index !== 0 && setSelectedNode(node.id)}
                onDrag={handleLocalNodeDrag}
                onDragEnd={handleLocalNodeDragEnd}
                onDelete={() => handleLocalNodeDelete(node.id)}
                onAddAfter={() => handleNodeAddAfter()}
                disabled={isLoading}
                disableDelete={node.id === '-1' || index === 1}
              />
            );
          });
        }, [nodes, selectedNode, handleLocalNodeDrag, handleLocalNodeDragEnd, isLoading, setSelectedNode, handleLocalNodeDelete])}
        
        </div> {/* Close transform div */}
      </div>

      {/* Workflow Settings Panel */}
      {showWorkflowSettings && (
        <WorkflowSettingsPanel
          workflow={workflow}
          onUpdate={onChange}
          onClose={() => setShowWorkflowSettings(false)}
        />
      )}

      {/* Node Configuration Panel */}
      {selectedNode && !showWorkflowSettings && (
        <NodeConfigPanel
          node={nodes.find(n => n.id === selectedNode)!}
          nodes={nodes}
          edges={edges}
          onUpdate={(updatedNode) => {
            const updatedNodes = nodes.map(n => n.id === selectedNode ? updatedNode : n);
            setNodes(updatedNodes);
          }}
          onEdgeUpdate={handleLocalEdgeUpdate}
          onClose={() => setSelectedNode(null)}
          onCreateNewNode={() => handleCreateNodeForConnection(selectedNode)}
        />
      )}

      {/* Node Palette */}
      {showNodePalette && (
        <NodePalette
          onAddNode={handleLocalNodeAdd}
          onClose={() => {
            setShowNodePalette(false);
            setConnectToNodeId(null);
          }}
        />
      )}

      <CanvasControls
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
        onAddNode={handleNodeAddAfter}
        onShowSettings={() => {
          setSelectedNode(null); // Close any open node config panel
          setShowWorkflowSettings(true);
        }}
      />
    </div>
  );
};

export const WorkflowCanvas = React.memo(WorkflowCanvasComponent, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.workflow.name === nextProps.workflow.name &&
    prevProps.workflow.graph === nextProps.workflow.graph &&
    prevProps.onChange === nextProps.onChange
  );
});